/* eslint-disable react/forbid-prop-types */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Paper from '@base/Paper/Paper';
import Typography from 'aquilla/core/atoms/Typography/Typography';
import AquillaButton from 'aquilla/core/molecules/Button';
import Image from '@base/Image';
import { TTU_NUMBER_CLICKED, TTU_POP_UP_CLICK } from '@constants/segment';
import { TalkState } from '@cont/Login/constants';
import { sendTTUEvents } from '@events/sendAnalytics';
import { evtNameCategoryMap, inboundCallNumber } from '@constants/index';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import {
  getTTUShowData,
  getShowTTUCurtain
} from '@stores/selectors/ttuSelectors';
import Call from 'aquilla/icons/actions/Call';
import { useCallback } from 'react';
import { segment } from '@actions/analytics';
import useTTUStore from '@stores/ttu';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import { emptyObject } from '@constants/empty';
import useWindowSize from '@hooks/useWindowSize';

const TTUContainer = styled(Paper)`
  max-width: 1136px;
  width: 100%;
  margin: 160px auto;
  position: relative;
  display: flex;
  padding: ${Spacings.SPACING_12B} ${Spacings.SPACING_16B};

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    margin: 0px !important;
    margin-top: ${Spacings.SPACING_4B} !important;
    margin-bottom: ${Spacings.SPACING_4B} !important;
    padding: 0px !important;
    padding: ${Spacings.SPACING_6B} !important;
  }
  justify-content: space-between;
`;

const AboutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: ${Spacings.SPACING_25B};
  ${mediaQuery.uptoMobile} {
    flex-direction: row;
    margin-right: 0px !important;
  }
`;

const StyledP1 = styled(Typography)`
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_10B};
  color: var(--color-text-primary);
  font-size: ${Fonts.LARGE};
`;

const IllustrationContainer = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
  margin-top: -${Spacings.SPACING_2B};
`;

const CallLabel = styled(Typography)`
  font-size: ${Fonts.H7};
  font-weight: 700;
  line-height: 150%;
  margin-bottom: ${Spacings.SPACING_1B};
  color: var(--color-text-secondary);
`;

const Options = styled.div`
  display: flex;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const StyledBtn = styled(AquillaButton)`
  margin-right: ${Spacings.SPACING_5B};
  background-color: var(--color-base-fill);
  color: var(--color-text-tertiary);
  ${mediaQuery.uptoMobile} {
    margin-right: 0px !important ;
  }
`;

const Divider = styled.div`
  width: 0;
  height: ${Spacings.SPACING_8B};
  margin-right: ${Spacings.SPACING_5B};
  border: ${Spacings.SPACING_1} solid var(--color-divider);
`;
const BottomDiv = styled.div`
  display: flex;
  flex-direction: column;
`;
const LeftDiv = styled.div``;

const Phone = styled.a`
  display: flex;
  width: fit-content;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
`;

const CallDiv = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_6B};
`;

const HaveQuestionAboutGoalHeading = styled(Typography)`
  color: var(--color-text-primary);
  width: 100%;
`;

const Banner = ({ goalDetails, className, ttuConfig = {} }) => {
  const { isMobile } = useWindowSize();

  const {
    title,
    subTitle = 'Our expert can answer all your questions',
    imgUrl = 'ttu/ttu_illustration_call.svg',
    ttuEventsProps = emptyObject,
    phone,
    buttonCTA,
    isOffline = false
  } = ttuConfig;
  const showData = useTTUStore(getTTUShowData);
  const showTTUCurtain = useTTUStore(getShowTTUCurtain);
  const meData = useMyInfo(getMe);

  let userType = showData?.isRepeatUser ? 'Repeat' : 'New';
  if (
    !showData?.isRepeatUser &&
    (showData?.state === TalkState.NO_SHOW ||
      showData?.state === TalkState.LOCAL_NO_SHOW)
  ) {
    userType = 'Scheduled';
  }
  const phoneNumber =
    phone ||
    (showData?.countryCode && showData?.phoneNumber
      ? `${showData?.countryCode} ${showData?.phoneNumber}`
      : inboundCallNumber);

  const onPhoneNumberClick = useCallback(() => {
    segment.track(
      TTU_NUMBER_CLICKED,
      {
        inbound_phone_number: phoneNumber,
        ...ttuEventsProps
      },
      evtNameCategoryMap.WITHOUT_CATEGORY
    );
  }, [phoneNumber, ttuEventsProps]);
  if (isMobile) {
    return (
      <TTUContainer className={className}>
        <AboutWrapper>
          <LeftDiv className="ttu-left">
            <HaveQuestionAboutGoalHeading variant="h2" className="ttu-title">
              {title || null}
              {!title && (
                <>
                  Have questions about
                  {` ${goalDetails?.name || ' '} subscription?`}
                </>
              )}
            </HaveQuestionAboutGoalHeading>
            <StyledP1 className="ttu-description">{subTitle}</StyledP1>
          </LeftDiv>
          <IllustrationContainer className="ttu-illustration-container">
            <Image alt="Talk To Unacademy" src={imgUrl} layout="fill" />
          </IllustrationContainer>
        </AboutWrapper>

        <BottomDiv>
          <Options>
            <StyledBtn
              label={buttonCTA || 'Get a call from us'}
              fullWidth
              size="large"
              onClick={() => {
                sendTTUEvents(
                  TTU_POP_UP_CLICK,
                  {
                    user_ttu_status: userType,
                    source: 'Bottom Home Screen',
                    last_primary_source_section: 'Bottom Home Screen',
                    ...ttuEventsProps
                  },
                  meData?.uid,
                  meData?.username,
                  goalDetails?.uid,
                  goalDetails?.name
                );
                if (goalDetails?.uid) {
                  showTTUCurtain(true, 'Bottom Home Screen');
                } else {
                  window.open(`/talktous?isOffline=${isOffline}`, '_blank');
                }
              }}
            />
            {phoneNumber && (
              <CallDiv className="ttu-call">
                <CallLabel>Or call us</CallLabel>
                <Phone href={`tel:${phoneNumber}`} onClick={onPhoneNumberClick}>
                  <Call
                    height={Spacings.SPACING_4B}
                    width={Spacings.SPACING_4B}
                    color="var(--color-text-primary)"
                  />
                  <Typography variant="h6">{phoneNumber}</Typography>
                </Phone>
              </CallDiv>
            )}
          </Options>
        </BottomDiv>
      </TTUContainer>
    );
  }
  return (
    <TTUContainer className={className}>
      <AboutWrapper>
        <HaveQuestionAboutGoalHeading variant="h2" className="ttu-title">
          {title || null}
          {!title && (
            <>
              Have questions about
              {` ${goalDetails?.name || ' '} subscription?`}
            </>
          )}
        </HaveQuestionAboutGoalHeading>
        <StyledP1 className="ttu-description">{subTitle}</StyledP1>

        <Options>
          <StyledBtn
            label="Get a call from us"
            size="large"
            onClick={() => {
              sendTTUEvents(
                TTU_POP_UP_CLICK,
                {
                  user_ttu_status: userType,
                  source: 'Bottom Home Screen',
                  last_primary_source_section: 'Bottom Home Screen',
                  ...ttuEventsProps
                },
                meData?.uid,
                meData?.username,
                goalDetails?.uid,
                goalDetails?.name
              );
              if (goalDetails?.uid) {
                showTTUCurtain(true, 'Bottom Home Screen');
              } else {
                window.open(`/talktous?isOffline=${isOffline}`, '_blank');
              }
            }}
          />
          {phoneNumber && (
            <>
              <Divider />
              <div>
                <CallLabel variant="p1">Or call us</CallLabel>
                <Phone href={`tel:${phoneNumber}`} onClick={onPhoneNumberClick}>
                  <Call
                    height={Spacings.SPACING_4B}
                    width={Spacings.SPACING_4B}
                    color="var(--color-text-primary)"
                  />
                  <Typography variant="h6">{phoneNumber}</Typography>
                </Phone>
              </div>
            </>
          )}
        </Options>
      </AboutWrapper>
      <IllustrationContainer className="ttu-illustration-container">
        <Image alt="Talk To Unacademy" src={imgUrl} layout="fill" />
      </IllustrationContainer>
    </TTUContainer>
  );
};

export default Banner;

Banner.propTypes = {
  goalDetails: PropTypes.objectOf(PropTypes.any).isRequired,
  className: PropTypes.string
};

Banner.defaultProps = {
  className: ''
};
